import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BackButton from "@/components/BackButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CalendarDays, ChevronLeft, ChevronRight, Download, List, Plus, Trash2, CheckCircle2, Undo2, Eye, Loader2, AlertTriangle } from "lucide-react";
import SchedulePaymentDialog from "@/components/SchedulePaymentDialog";
import FileViewer from "@/components/FileViewer";
import { formatNumber } from "@/lib/numberFormatting";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { loadOrganizations, type Organization } from "@/lib/organizations";
import {
  deleteScheduledPayment, downloadPaymentAttachment, loadScheduledPayments, paymentUrgency,
  todayIso, updateScheduledPayment, type PaymentUrgency, type ScheduledPayment,
} from "@/lib/scheduledPayments";

const ALL = "__all__";
const URGENCY_STYLE: Record<PaymentUrgency, string> = {
  overdue: "border-destructive bg-destructive/10 text-destructive",
  today: "border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  soon: "border-primary/40 bg-primary/5 text-foreground",
  later: "border-border bg-muted/40 text-foreground",
  paid: "border-border bg-muted/20 text-muted-foreground line-through",
};
const URGENCY_LABEL: Record<PaymentUrgency, string> = {
  overdue: "Vencido", today: "Hoy", soon: "Próximos 7 días", later: "Programado", paid: "Pagado",
};
const money = (n: number) => `${formatNumber(n)} €`;
const fmtDate = (iso: string | null) => (iso ? iso.split("-").reverse().join("/") : "—");
const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const AgendaPagos = () => {
  const { toast } = useToast();
  const { organizationId } = useAuth();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState<string>("");
  const [allCompanies, setAllCompanies] = useState<{ id: string; name: string; organization_id: string }[]>([]);
  const [companyId, setCompanyId] = useState<string>(ALL);
  const [allPayments, setAllPayments] = useState<ScheduledPayment[]>([]);
  const [view, setView] = useState<"calendar" | "list">("list");
  const [showPaid, setShowPaid] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => {
    supabase.from("company_masters").select("id, name, organization_id").order("name").then(({ data }) => setAllCompanies((data as any) ?? []));
    loadOrganizations().then(setOrgs);
  }, []);

  // Asesorías con empresas visibles; por defecto la del usuario.
  const visibleOrgs = useMemo(() => orgs.filter((o) => allCompanies.some((c) => c.organization_id === o.id)), [orgs, allCompanies]);
  useEffect(() => {
    if (orgId || visibleOrgs.length === 0) return;
    setOrgId(visibleOrgs.find((o) => o.id === organizationId)?.id ?? visibleOrgs[0].id);
  }, [visibleOrgs, organizationId, orgId]);

  const companies = useMemo(() => allCompanies.filter((c) => c.organization_id === orgId), [allCompanies, orgId]);
  const payments = useMemo(() => {
    const ids = new Set(companies.map((c) => c.id));
    return allPayments.filter((p) => ids.has(p.company_id));
  }, [allPayments, companies]);

  const reload = useCallback(async () => {
    try {
      setAllPayments(await loadScheduledPayments(companyId === ALL ? undefined : companyId));
    } catch (e: any) {
      toast({ title: "No se pudo cargar la agenda", description: e?.message, variant: "destructive" });
    }
  }, [companyId, toast]);
  useEffect(() => { reload(); }, [reload]);

  const today = todayIso();
  const companyName = (id: string) => companies.find((c) => c.id === id)?.name ?? "";
  const pending = payments.filter((p) => p.status === "pending");
  const overdue = pending.filter((p) => p.due_date < today);
  const dueToday = pending.filter((p) => p.due_date === today);
  const visible = showPaid ? payments : pending;
  const sum = (arr: ScheduledPayment[]) => arr.reduce((a, p) => a + Number(p.amount || 0), 0);

  const markPaid = async (p: ScheduledPayment, paid: boolean) => {
    try {
      await updateScheduledPayment(p.id, { status: paid ? "paid" : "pending", paid_at: paid ? new Date().toISOString() : null });
      reload();
    } catch (e: any) { toast({ title: "Error", description: e?.message, variant: "destructive" }); }
  };
  const remove = async (p: ScheduledPayment) => {
    if (!window.confirm(`¿Eliminar el pago de ${p.supplier_name || "este proveedor"}?`)) return;
    try { await deleteScheduledPayment(p); reload(); }
    catch (e: any) { toast({ title: "Error", description: e?.message, variant: "destructive" }); }
  };
  const openFile = async (p: ScheduledPayment) => {
    try {
      const blob = await downloadPaymentAttachment(p);
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = p.file_name || "factura.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e: any) { toast({ title: "No se pudo descargar", description: e?.message, variant: "destructive" }); }
  };
  const previewAttachment = async (p: ScheduledPayment) => {
    if (previewLoading) return;
    setPreviewLoading(p.id);
    try {
      const blob = await downloadPaymentAttachment(p);
      if (!blob) return;
      setPreviewFile(new File([blob], p.file_name || "factura.pdf", { type: blob.type || "application/pdf" }));
    } catch (e: any) {
      toast({ title: "No se pudo abrir la factura", description: e?.message, variant: "destructive" });
    } finally {
      setPreviewLoading(null);
    }
  };

  const calendarCells = useMemo(() => {
    const first = new Date(month);
    const startOffset = (first.getDay() + 6) % 7; // lunes primero
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const cells: (string | null)[] = Array(startOffset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(todayIso(new Date(first.getFullYear(), first.getMonth(), d)));
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [month]);

  const byDay = useMemo(() => {
    const m = new Map<string, ScheduledPayment[]>();
    visible.forEach((p) => m.set(p.due_date, [...(m.get(p.due_date) ?? []), p]));
    return m;
  }, [visible]);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <BackButton variant="ghost" className="text-xs" />
        <h1 className="text-2xl font-semibold font-[Playfair_Display,serif] mr-auto">Agenda de pagos</h1>
        {visibleOrgs.length > 1 && (
          <Select value={orgId} onValueChange={(v) => { setOrgId(v); setCompanyId(ALL); }}>
            <SelectTrigger className="w-[220px] h-9"><SelectValue placeholder="Asesoría" /></SelectTrigger>
            <SelectContent>
              {visibleOrgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-[260px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas las empresas de la asesoría</SelectItem>
            {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setDialogOpen(true)} disabled={companyId === ALL} title={companyId === ALL ? "Elige una empresa para añadir un pago" : undefined}>
          <Plus className="h-4 w-4 mr-1.5" /> Añadir pago
        </Button>
      </div>

      {(overdue.length > 0 || dueToday.length > 0) && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
          <span>
            {dueToday.length > 0 && <>Hoy hay <b>{dueToday.length}</b> pago(s) por <b>{money(sum(dueToday))}</b>. </>}
            {overdue.length > 0 && <>Hay <b>{overdue.length}</b> pago(s) vencido(s) sin marcar como pagados por <b>{money(sum(overdue))}</b>.</>}
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Vencidos", overdue], ["Hoy", dueToday],
          ["Próximos 7 días", pending.filter((p) => paymentUrgency(p, today) === "soon")],
          ["Total pendiente", pending],
        ].map(([label, arr]) => (
          <div key={label as string} className="rounded-lg border bg-card p-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label as string}</p>
            <p className="text-lg font-semibold tabular-nums">{money(sum(arr as ScheduledPayment[]))}</p>
            <p className="text-xs text-muted-foreground">{(arr as ScheduledPayment[]).length} pago(s)</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={view === "list" ? "default" : "outline"} onClick={() => setView("list")}><List className="h-4 w-4 mr-1.5" />Lista</Button>
        <Button size="sm" variant={view === "calendar" ? "default" : "outline"} onClick={() => setView("calendar")}><CalendarDays className="h-4 w-4 mr-1.5" />Calendario</Button>
        <label className="ml-auto flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showPaid} onChange={(e) => setShowPaid(e.target.checked)} /> Mostrar pagados
        </label>
      </div>

      {view === "list" ? (
        <div className="rounded-lg border bg-card divide-y">
          {visible.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No hay pagos programados.</p>}
          {visible.map((p) => {
            const u = paymentUrgency(p, today);
            return (
              <div key={p.id} className="p-3 flex flex-wrap items-center gap-3">
                <Badge variant="outline" className={cn("shrink-0", URGENCY_STYLE[u])}>{URGENCY_LABEL[u]}</Badge>
                <div className="w-24 text-sm tabular-nums">{fmtDate(p.due_date)}</div>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-medium">{p.supplier_name || "—"} <span className="text-muted-foreground font-normal">· Fra. {p.invoice_number || "—"}</span></p>
                  <p className="text-xs text-muted-foreground">
                    {companyId === ALL && <>{companyName(p.company_id)} · </>}
                    {p.bank_account && <>IBAN {p.bank_account} · </>}
                    {p.authorized_by && <>Autoriza: {p.authorized_by} · </>}
                    {p.notes}
                  </p>
                </div>
                <div className="text-sm font-semibold tabular-nums w-28 text-right">{money(Number(p.amount))}</div>
                <div className="flex gap-1">
                  {p.file_path && <>
                    <Button size="sm" variant="outline" onClick={() => previewAttachment(p)} disabled={previewLoading !== null}>
                      {previewLoading === p.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}Ver factura
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openFile(p)} title="Descargar factura" aria-label="Descargar factura"><Download className="h-4 w-4" /></Button>
                  </>}
                  {p.status === "pending" ? (
                    <Button size="sm" onClick={() => markPaid(p, true)}><CheckCircle2 className="h-4 w-4 mr-1" />Marcar pagado</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => markPaid(p, false)}><Undo2 className="h-4 w-4 mr-1" />Deshacer</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => remove(p)} title="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border bg-card p-3">
          <div className="flex items-center justify-between mb-3">
            <Button size="sm" variant="outline" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
            <p className="font-medium">{MONTHS[month.getMonth()]} {month.getFullYear()}</p>
            <Button size="sm" variant="outline" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="overflow-x-auto">
            <div className="grid grid-cols-7 gap-1 min-w-[700px]">
              {["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"].map((d) => <div key={d} className="text-[11px] text-center text-muted-foreground uppercase">{d}</div>)}
              {calendarCells.map((iso, i) => (
                <div key={i} className={cn("min-h-[96px] rounded border p-1 text-xs", !iso && "bg-muted/20 border-transparent", iso === today && "border-primary border-2")}>
                  {iso && <>
                    <div className="text-right text-muted-foreground">{Number(iso.slice(8))}</div>
                    <div className="space-y-1">
                      {(byDay.get(iso) ?? []).map((p) => (
                        <div key={p.id} className={cn("rounded border px-1 py-0.5", URGENCY_STYLE[paymentUrgency(p, today)])} title={`${p.supplier_name} · ${money(Number(p.amount))}`}>
                          <p className="truncate">{p.supplier_name || "—"} · {money(Number(p.amount))}</p>
                          {p.file_path && <Button size="sm" variant="ghost" className="h-6 px-1 text-xs w-full justify-start" onClick={() => previewAttachment(p)} disabled={previewLoading !== null}>
                            {previewLoading === p.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}Ver factura
                          </Button>}
                        </div>
                      ))}
                    </div>
                  </>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <SchedulePaymentDialog open={dialogOpen} onOpenChange={setDialogOpen} companyId={companyId === ALL ? null : companyId} onSaved={reload} />
      <FileViewer file={previewFile} open={previewFile !== null} onClose={() => setPreviewFile(null)} />
    </div>
  );
};

export default AgendaPagos;
